import { type ReactElement, useCallback } from 'react';
import BaseDatePicker from '@creditflowresearch/uilib/Picker/BaseDatePicker';
import { WEEKDAY_SHORT_MONTH_DAY_YEAR_FORMAT } from '@/creditflowresearch/ui/components/corporate-bond-screener/Sidebar/Sections/constants/dateFormats';
import Flex from '@creditflowresearch/uilib/Flex';
import FilterSection from '@/creditflowresearch/ui/common/components/templates/FilterSection';
import FilterSectionHeader from '@/creditflowresearch/ui/common/components/organisms/FilterSectionHeader';
import { FilterSectionIds } from '@/creditflowresearch/ui/components/corporate-bond-screener/Sidebar/Sections/constants';
import DateInput from '@/creditflowresearch/ui/common/components/DatePicker/DateInput';
import DatePickerHeader from '@/creditflowresearch/ui/common/components/DatePicker/DatePickerHeader';
import useMaturityDate from './useMaturityDate';

import styles from '@/creditflowresearch/ui/common/components/DatePicker/styles/dateRangeFilter.module.scss';

const MaturityDate = (): ReactElement => {
  const { fromDate, toDate, minDate, maxDate, handleFromChange, handleToChange } = useMaturityDate();

  const clearFrom = useCallback(() => handleFromChange(null), [handleFromChange]);
  const clearTo = useCallback(() => handleToChange(null), [handleToChange]);

  return (
    <FilterSection
      sectionId={ FilterSectionIds.MaturityDate }
      header={ <FilterSectionHeader title="MATURITY DATE" /> }
      dataTest="maturityDateSection"
    >
      <Flex align="center" gap={ 8 }>
        <div className={ styles.datePicker }>
          <BaseDatePicker
            name="maturityDateFrom"
            customInput={ <DateInput name="maturityDateFrom" dataTest="maturityDateFromInput" label="FROM" onClear={ clearFrom } /> }
            renderCustomHeader={ (props) => (
              <DatePickerHeader { ...props } minDate={ minDate } maxDate={ toDate || maxDate } />) }
            dateFormat={ WEEKDAY_SHORT_MONTH_DAY_YEAR_FORMAT }
            placeholderText="Select Date"
            selected={ fromDate }
            onChange={ handleFromChange }
            minDate={ minDate }
            maxDate={ toDate || maxDate }
            openToDate={ fromDate || minDate }
            popperPlacement="bottom-start"
            dataTest="maturityDateFromPicker"
          />
        </div>
        <Flex direction="column" shrink="0">
          <span className={ styles.label }>&nbsp;</span>
          <span className={ styles.separator }>-</span>
        </Flex>
        <div className={ styles.datePicker }>
          <BaseDatePicker
            name="maturityDateTo"
            customInput={ <DateInput name="maturityDateTo" dataTest="maturityDateToInput" label="TO" onClear={ clearTo } /> }
            renderCustomHeader={ (props) => (
              <DatePickerHeader { ...props } minDate={ fromDate || minDate } maxDate={ maxDate } />) }
            dateFormat={ WEEKDAY_SHORT_MONTH_DAY_YEAR_FORMAT }
            placeholderText="Select Date"
            selected={ toDate }
            onChange={ handleToChange }
            minDate={ fromDate || minDate }
            maxDate={ maxDate }
            popperPlacement="bottom-end"
            dataTest="maturityDateToPicker"
          />
        </div>
      </Flex>
    </FilterSection>
  );
};

export default MaturityDate;
